<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import { type SuperUserOrganisation as Organisation } from '@/types/generated/types.gen'
  import { OrganisationsService } from '@/types/generated/services.gen'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import Badge from '@/components/ui/badge/Badge.vue'
  import * as yup from 'yup'
  import { onMounted, ref } from 'vue'
  import { genericErrorToast } from '@/lib/toasts'
  import { AdvancedMarker, GoogleMap, InfoWindow } from 'vue3-google-map'
  import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
  import { Button } from '@/components/ui/button'

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

  const mapRef = ref<GoogleMap>(null)

  const loadingOrganisations = ref(false)
  const organisations = ref<Organisation[]>([])
  const searchAddress = ref('')

  const loadingMap = ref(true)
  const mapOptions = ref({ center: { lat: 51.165691, lng: 10.451526 }, zoom: 6 })

  const fetchOrganisations = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const response = await OrganisationsService.organisationsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }

  const getGeoLocation = async (
    street: string,
    streetNr: string,
    city: string,
    zip: string
  ): Promise<LatLng | null> => {
    const geocoder = new google.maps.Geocoder()
    const address = `${street} ${streetNr}, ${city}, ${zip}`
    try {
      const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: address }, (results, status) => {
          if (status === 'OK') {
            resolve(results)
          } else {
            reject(new Error('Geocode was not successful: ' + status))
          }
        })
      })
      return results[0].geometry.location
    } catch (error) {
      console.error(error)
      genericErrorToast()
      return null
    }
  }

  const handleAddressInput = (value: string) => {
    searchAddress.value = value
    getGeoLocation(value, '', '', '').then((location: LatLng) => {
      mapOptions.value.center = location
      mapOptions.value.zoom = 10
    })
  }

  const getPosition = (org: Organisation): LatLng | null => {
    // The marker is not drawn if value is null
    if (!org.latitude || !org.longitude) {
      return null
    }
    return { lat: parseFloat(org.latitude), lng: parseFloat(org.longitude) }
  }

  onMounted(async () => {
    loadingOrganisations.value = true
    fetchOrganisations({ limit: 0, offset: 0 })
      .then((response) => {
        organisations.value = response.results as Organisation[]
        loadingOrganisations.value = false
      })
      .catch((e) => {
        genericErrorToast()
        console.log(e)
        loadingOrganisations.value = false
      })
    // Loading map before mounting element throws error
    loadingMap.value = false
  })
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Map', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_MAP }
          ]"
        />
      </div>
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div v-if="loadingOrganisations || loadingMap">
          <div class="flex items-center justify-center">Loading...</div>
        </div>
        <div v-else>
          <div class="flex gap-3 pb-4">
            <div class="grow">
              <InputField
                v-model="searchAddress"
                label="Adresse"
                name="Adresse"
                :placeholder="'Bitte geben Sie eine Adresse ein'"
                :rules="yup.string()"
              ></InputField>
            </div>
            <Button
              class="mt-auto h-[38px] gap-x-3"
              variant="default"
              size="lg"
              :disabled="!searchAddress || loadingMap"
              @click="handleAddressInput(searchAddress)"
            >
              <span>Suchen</span>
              <MagnifyingGlassIcon class="size-4" />
            </Button>
          </div>
          <GoogleMap
            ref="mapRef"
            style="width: 100%; height: 500px"
            map-id="customers-map"
            :street-view-control="false"
            :api-key="GOOGLE_MAPS_API_KEY"
            :center="mapOptions.center"
            :zoom="mapOptions.zoom"
          >
            <AdvancedMarker
              v-for="org in organisations"
              :key="org.id"
              :options="{
                position: getPosition(org),
                title: org.name
              }"
              :pin-options="{
                background: org.has_churned ? 'grey' : 'red',
                borderColor: org.has_churned ? 'black' : 'darkred',
                glyphColor: 'white'
              }"
            >
              <InfoWindow class="max-w-[200px]">
                <div>
                  <div class="flex items-center gap-2 pb-2">
                    <h1 class="text-lg">{{ org.name }}</h1>
                    <Badge
                      v-if="org.has_churned"
                      class="h-5"
                    >
                      Gekündigt
                    </Badge>
                  </div>
                  <p>{{ org.street }} {{ org.street_number }}, {{ org.zip }} {{ org.city }}</p>
                  <p>
                    <a
                      :href="'tel:' + org.phone"
                      class="text-blue-500"
                    >
                      {{ org.phone }}
                    </a>
                  </p>
                  <p>{{ org.email }}</p>
                </div>
              </InfoWindow>
            </AdvancedMarker>
          </GoogleMap>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
